import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Input, Tag, theme, Tooltip } from "antd";
import { MESSAGE_VALIDATION } from "common/common";

const TagInput = forwardRef(({ tags, setTags }, ref) => {
	const { token } = theme.useToken();
	const [inputVisible, setInputVisible] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [inputError, setInputError] = useState(undefined);
	const inputRef = useRef(null);
	const colorPrimary = token.colorPrimary;

	const handleClose = (removedTag) => {
		const newTags = tags?.filter((tag) => tag !== removedTag);
		setTags(newTags);
	};

	const showInput = () => {
		setInputVisible(true);
		setInputError(undefined);
	};

	const handleInputChange = (e) => {
		setInputValue(e.target.value);
		setInputError(undefined);
	};

	const handleInputConfirm = () => {
		const newTag = inputValue.trim();
		if (newTag) {
			if (tags?.includes(newTag)) {
				setInputError(MESSAGE_VALIDATION.LINE_ID_EXISTED);
				return;
			}
			setTags([...(Array.isArray(tags) ? tags : []), newTag]);
		}
		setInputVisible(undefined);
		setInputValue("");
		setInputError(undefined);
	};

	const handleRemoveTag = (e, tag) => {
		e.preventDefault();
		handleClose(tag);
		if (tag === inputValue.trim()) {
			setInputError(undefined);
		}
	};

	const forMap = (tag) => (
		<span key={tag} className="inline-block max-w-full break-all">
			<Tag
				closable
				onClose={(e) => handleRemoveTag(e, tag)}
				className="!text-sm mb-2 break-words whitespace-normal"
			>
				{tag}
			</Tag>
		</span>
	);

	const tagChild = tags?.map(forMap);

	const tagPlusStyle = {
		background: token.colorBgContainer,
		borderStyle: "dashed",
	};

	useImperativeHandle(ref, () => ({
		confirmInput: handleInputConfirm,
		getCurrentInput: () => inputValue.trim(),
	}));

	useEffect(() => {
		if (inputVisible) {
			inputRef.current?.focus();
		}
	}, [inputVisible]);

	return (
		<div>
			<div className="font-bold text-black mb-4">
				<p>
					<span className="font-bold">管理者追加</span>
					<Tooltip
						title={
							<div className="whitespace-pre-line">
								{`※ LINE WORKSに設定されている自分のトークIDを登録してください。\n※ 自分のトークIDの登録は上限10個です。`}
							</div>
						}
					>
						<QuestionCircleOutlined
							style={{ color: colorPrimary }}
							className="ml-2 mr-4"
						/>
					</Tooltip>
				</p>
			</div>
			<div
				className={`border border-gray-200 rounded-md p-4 ${tags?.length >= 10 && "pb-2"}`}
			>
				<div className="flex flex-wrap">{tagChild}</div>
				{inputVisible ? (
					<div>
						<Input
							ref={inputRef}
							size="middle"
							style={{ width: 360 }}
							value={inputValue}
							onChange={handleInputChange}
							onPressEnter={handleInputConfirm}
							status={inputError ? "error" : ""}
							className="text-sm"
							placeholder="LINE WORKSに設定されている自分のトークIDを入力"
						/>
						{inputError && (
							<div className="text-red-500 text-xs mt-1">{inputError}</div>
						)}
					</div>
				) : (
					(tags?.length < 10 || !tags) && (
						<Button className="size-8" onClick={showInput} style={tagPlusStyle}>
							<PlusOutlined />
						</Button>
					)
				)}
			</div>
		</div>
	);
});

export default TagInput;
